// Pure helpers for the default-syllabi screen (no React, unit tested).
import { LEVEL_ORDER, levelRank } from '../../helper/competitionSort';
import { agesCode22, levelData } from '../CreateFeisEvent/Components/SyllabusComp/SyllabusArrays';
import { suggestChampionship } from '../../helper/championship';

// Levels (competition groups) offered when adding a row, beyond those already
// in the region.
export const KNOWN_GROUPS = [...LEVEL_ORDER, 'ARTS AND CULTURE'];
export const AGE_OPTIONS = agesCode22;
export const ELIGIBLE_LEVELS = levelData.map((l) => l.toUpperCase());

export { suggestChampionship };

// Rows grouped by level in canonical order (rows arrive sorted from the API).
export function groupRows(rows) {
  const byGroup = new Map();
  for (const r of rows || []) {
    const g = r.group || '';
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g).push(r);
  }
  return [...byGroup.entries()]
    .sort(([a], [b]) => levelRank(a) - levelRank(b) || a.localeCompare(b))
    .map(([group, list]) => ({ group, rows: list }));
}

export function matchesSearch(row, q) {
  const s = String(q || '').trim().toLowerCase();
  if (!s) return true;
  return [row.code, row.description, row.group, ...(row.ages || [])]
    .some((v) => String(v || '').toLowerCase().includes(s));
}

const ageIndex = (a) => AGE_OPTIONS.indexOf(String(a).toUpperCase());

// ["U5","U6","U7"] -> "U5–U7"; gaps or unknown ages are listed.
export function agesLabel(ages) {
  const list = [...(ages || [])].sort((a, b) => ageIndex(a) - ageIndex(b));
  if (list.length === 0) return '—';
  const idx = list.map(ageIndex);
  const contiguous = idx.every((v, i) => v >= 0 && (i === 0 || v === idx[i - 1] + 1));
  if (list.length > 2 && contiguous) return `${list[0]}–${list[list.length - 1]}`;
  return list.join(', ');
}

export function dancersLabel(row) {
  if (row.girls && row.boys) return 'All';
  if (row.girls) return 'Girls';
  if (row.boys) return 'Boys';
  return 'Nobody';
}

export function formatRules(row) {
  const r = Number(row.rounds) || 1;
  const j = Number(row.adjudicators) || 1;
  return `${r} ${r === 1 ? 'round' : 'rounds'} · ${j} ${j === 1 ? 'judge' : 'judges'}`;
}

export const emptyForm = (group = '') => ({
  group,
  code: '',
  description: '',
  ages: [],
  levels: group && ELIGIBLE_LEVELS.includes(group.toUpperCase()) ? [group.toUpperCase()] : [],
  girls: true,
  boys: true,
  rounds: 1,
  adjudicators: 1,
  price: '0',
  max_participants: '0',
  second_chance: false,
  championship: suggestChampionship({ group, levels: group && ELIGIBLE_LEVELS.includes(group.toUpperCase()) ? [group.toUpperCase()] : [] }),
  championshipTouched: false,
});

export const rowToForm = (row) => ({
  group: row.group || '',
  code: row.code || '',
  description: row.description || '',
  ages: [...(row.ages || [])],
  levels: [...(row.levels || [])].map((l) => String(l).toUpperCase()),
  girls: row.girls !== false,
  boys: row.boys !== false,
  rounds: Number(row.rounds) || 1,
  adjudicators: Number(row.adjudicators) || 1,
  price: String(row.price ?? 0),
  max_participants: String(row.max_participants ?? 0),
  second_chance: !!row.second_chance,
  championship: !!row.championship,
  championshipTouched: true,
});

// Form -> API payload, or { errors } keyed by field. Mirrors the backend's
// validation so problems show next to the field before saving.
export function formToPayload(form) {
  const errors = {};
  const group = String(form.group || '').trim();
  const code = String(form.code || '').trim();
  const description = String(form.description || '').trim();
  if (!group) errors.group = 'Choose a level.';
  if (!code) errors.code = 'Enter a code.';
  if (!description) errors.description = 'Enter a description.';
  if (!form.ages || form.ages.length === 0) errors.ages = 'Pick at least one age.';
  if (!form.girls && !form.boys) errors.dancers = 'Open it to girls, boys or both.';
  const price = Number(form.price);
  if (String(form.price).trim() === '' || !Number.isFinite(price) || price < 0) errors.price = 'Enter 0 or more.';
  const max = Number(form.max_participants);
  if (String(form.max_participants).trim() === '' || !Number.isInteger(max) || max < 0) {
    errors.max_participants = 'Enter a whole number (0 = no limit).';
  }
  if (Object.keys(errors).length) return { errors };
  return {
    payload: {
      group,
      code,
      description,
      ages: form.ages,
      levels: form.levels,
      girls: !!form.girls,
      boys: !!form.boys,
      rounds: Number(form.rounds),
      adjudicators: Number(form.adjudicators),
      price,
      max_participants: max,
      second_chance: !!form.second_chance,
      championship: !!form.championship,
    },
  };
}
